import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Heart, Package, Film, Radio, ArrowLeft } from "lucide-react";
import { api } from "../lib/api";
import { fmtMoney } from "../lib/money";
import { useAuth } from "../lib/AuthContext";
import SaveButton from "../components/SaveButton";

const KINDS = {
  product: {
    label: "Saved products",
    icon: Package,
    color: "#D4FF00",
    idKey: "product_id",
    href: (it) => `/product/${it.product_id}`,
    title: (it) => it.name,
    sub: (it) => `${fmtMoney(it.price)} · ${it.store_name || ""}`,
    img: (it) => it.images?.[0],
  },
  video: {
    label: "Saved videos",
    icon: Film,
    color: "#00F0FF",
    idKey: "video_id",
    href: (it) => `/videos`,    // feed; opening direct video would need router change
    title: (it) => it.title,
    sub: (it) => it.store_name || "",
    img: (it) => it.thumbnail_url,
  },
  stream: {
    label: "Saved live streams",
    icon: Radio,
    color: "#FF007F",
    idKey: "stream_id",
    href: (it) => `/live/${it.stream_id}`,
    title: (it) => it.title,
    sub: (it) => `${it.host_name || ""} · ${it.is_live ? "🔴 Live now" : "Offline"}`,
    img: (it) => it.thumbnail_url,
  },
};

export default function SavedPage() {
  const { kind } = useParams();
  const { user } = useAuth();
  const def = KINDS[kind];
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    if (!user) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/me/saved/${kind}`);
      setItems(data.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind, user]);

  if (!def) {
    return <div className="p-10 text-center text-[#A1A1AA]">Unknown saved category.</div>;
  }
  if (!user) {
    return (
      <div className="max-w-md mx-auto p-10 text-center">
        <Heart size={32} className="mx-auto text-[#FF007F] mb-3"/>
        <div className="font-display text-2xl mb-2">Login to see saved items</div>
        <Link to="/login" className="btn-primary">Login</Link>
      </div>
    );
  }
  const Icon = def.icon;

  return (
    <div className="pb-24 md:pb-12 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
      <Link to="/profile" className="text-xs text-[#A1A1AA] hover:text-[#D4FF00] mb-2 inline-flex items-center gap-1" data-testid="saved-back">
        <ArrowLeft size={12}/> Back to profile
      </Link>
      <div className="flex items-center gap-3 mb-6">
        <Icon size={32} style={{ color: def.color }}/>
        <h1 className="font-display font-black text-3xl sm:text-5xl uppercase">
          {def.label}<span style={{ color: def.color }}>.</span>
        </h1>
      </div>

      <div className="flex gap-2 mb-6 overflow-x-auto no-scrollbar">
        {Object.entries(KINDS).map(([k, d]) => (
          <Link key={k} to={`/saved/${k}`} data-testid={`saved-tab-${k}`}
            className={`shrink-0 chip text-xs ${kind === k ? "active" : ""}`}>
            <d.icon size={12}/> {d.label.replace("Saved ", "")}
          </Link>
        ))}
      </div>

      {loading && <div className="text-center text-[#A1A1AA] p-8">Loading…</div>}
      {!loading && items.length === 0 && (
        <div className="card p-10 text-center" data-testid={`saved-empty-${kind}`}>
          <Heart size={28} className="mx-auto text-[#A1A1AA] mb-2"/>
          <div className="font-display text-xl">Nothing saved yet</div>
          <div className="text-sm text-[#A1A1AA] mt-1">Tap the heart icon on any {kind} to save it for later.</div>
        </div>
      )}

      {kind === "product" && items.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="saved-products-grid">
          {items.map(p => (
            <div key={p.product_id} className="relative group">
              <Link to={`/product/${p.product_id}`} className="card overflow-hidden block hover:border-[#D4FF00] transition">
                <img src={p.images?.[0]} className="w-full aspect-square object-cover" alt=""/>
                <div className="p-3">
                  <div className="text-xs font-bold line-clamp-1">{p.name}</div>
                  <div className="text-xs text-[#A1A1AA] mt-1">{p.store_name}</div>
                  <div className="text-[#D4FF00] font-display font-bold mt-1">{fmtMoney(p.price)}</div>
                </div>
              </Link>
              <div className="absolute top-2 right-2">
                <SaveButton kind="product" id={p.product_id} variant="overlay" onChange={(s) => !s && load()}/>
              </div>
            </div>
          ))}
        </div>
      )}

      {(kind === "video" || kind === "stream") && items.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid={`saved-${kind}-grid`}>
          {items.map(it => {
            const idVal = it[def.idKey];
            return (
              <div key={idVal} className="relative group">
                <Link to={def.href(it)} className="card overflow-hidden block hover:border-[#D4FF00] transition">
                  <div className="relative aspect-[9/16]">
                    <img src={def.img(it)} className="w-full h-full object-cover" alt=""/>
                    {kind === "stream" && it.is_live && (
                      <span className="live-badge absolute top-2 left-2"><span className="dot"/>LIVE</span>
                    )}
                  </div>
                  <div className="p-3">
                    <div className="text-xs font-bold line-clamp-2">{def.title(it)}</div>
                    <div className="text-xs text-[#A1A1AA] mt-1 line-clamp-1">{def.sub(it)}</div>
                  </div>
                </Link>
                <div className="absolute top-2 right-2">
                  <SaveButton kind={kind} id={idVal} variant="overlay" onChange={(s) => !s && load()}/>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
